/**
 * The Active + Invite row's data (specs/home-page Story 5): selects over the Arena's challenge list
 * (no extra endpoint) and shapes the two tile models. The active model reads the clock (started,
 * score visibility, games left), so it is re-shaped the moment the next kickoff passes and the list
 * is refetched then for the fresh score (AC-37).
 */

import { useMemo } from 'react';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useChallengesQuery } from '../../challenges/hooks/useChallengesQuery';
import { challengesQueryKeys } from '../../challenges/constants/challengesQueryKeys';
import {
  selectActiveChallenge,
  selectOldestInvite,
  buildActiveTileModel,
  buildInviteTileModel,
  nextKickoffIso,
  type ChallengeWithParticipantMaps,
} from '../helpers/homeChallengeTiles.helper';
import type { HomeActiveTileModel, HomeInviteTileModel } from '../types/home.types';
import { useRefetchAt } from './useRefetchAt';
import { useTickAt } from './useTickAt';

export interface UseHomeChallengeTilesReturn {
  active: HomeActiveTileModel | null;
  invite: HomeInviteTileModel | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export const useHomeChallengeTiles = (): UseHomeChallengeTilesReturn => {
  const { userId } = useSupabaseAuth();
  const query = useChallengesQuery(userId ?? null, !!userId);
  const challenges = query.data;

  const activeChallenge = useMemo(
    () => (userId && challenges ? (selectActiveChallenge(challenges, userId) as ChallengeWithParticipantMaps | null) : null),
    [challenges, userId],
  );
  // Re-shape at the next kickoff (a refetch alone would not: unchanged data keeps its identity), and
  // refetch then so the score and status come from the server, not from a stale list.
  const nextKickoff = useMemo(() => (activeChallenge ? nextKickoffIso(activeChallenge, Date.now()) : null), [activeChallenge]);
  const kickoffTick = useTickAt(nextKickoff);
  useRefetchAt(nextKickoff, challengesQueryKeys.list(userId ?? null));

  const { active, invite } = useMemo(() => {
    if (!userId || !challenges) return { active: null, invite: null };
    const nowMs = Date.now();
    const inviteChallenge = selectOldestInvite(challenges, userId) as ChallengeWithParticipantMaps | null;
    return {
      active: activeChallenge ? buildActiveTileModel(activeChallenge, userId, nowMs) : null,
      invite: inviteChallenge ? buildInviteTileModel(inviteChallenge) : null,
    };
    // kickoffTick is the clock input: it changes exactly when `nowMs` would change the model.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenges, userId, activeChallenge, kickoffTick]);

  return {
    active,
    invite,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
};
